/**
 * Singleton - AlertScheduleManager
 * Implementa los escenarios de AOSPCL-189:
 * - Alerta con duración (visible_seconds)
 * - Alerta indefinida (sin visible_seconds o = 0)
 * - Alerta permanente (schedule vacío/ausente con enable=true)
 * - Fallback a alerta permanente (fechas mal formadas)
 * - Tiempo restante cuando se inicia durante evento vigente
 * - Configuración dinámica sin reiniciar app
 *
 */
export const AlertScheduleManager = (function() {

  let instance = null;

  function AlertScheduleManagerClass() {
    this.config = null;
    this.onShowModal = function() {};
    this.onHideModal = function() {};
    this.intervalId = null;
    this.isModalVisible = false;
    this.currentData = null;
    this.startedAt = null;
    this.wasJoinedInProgress = false;
  }

  AlertScheduleManagerClass.prototype = {

    /**
     * Inicializa el manager con la configuración
     * @param {Object|null} config - Configuración { enable, schedule }
     */
    init: function(config) {
      this.config = config;
      return this;
    },

    /**
     * Establece el callback para mostrar modal
     * @param {Function} callback - Recibe { type, remainingSeconds }
     */
    setOnShowModal: function(callback) {
      this.onShowModal = callback || function() {};
      return this;
    },

    /**
     * Establece el callback para ocultar modal
     * @param {Function} callback
     */
    setOnHideModal: function(callback) {
      this.onHideModal = callback || function() {};
      return this;
    },

    /**
     * Inicia la verificación periódica
     * @param {number} checkIntervalMs - Intervalo en ms (default: 1000)
     */
    start: function(checkIntervalMs) {
      const self = this;
      const interval = checkIntervalMs || 1000;

      if (this.intervalId) {
        return this;
      }

      this.startedAt = Date.now();

      this.check();

      this.intervalId = setInterval(function() {
        self.check();
      }, interval);

      return this;
    },

    /**
     * Detiene la verificación
     */
    stop: function() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
      return this;
    },

    /**
     * Actualiza la configuración en tiempo real
     * @param {Object|null} newConfig
     */
    updateConfig: function(newConfig) {
      this.config = newConfig;
      this.check();
      return this;
    },

    /**
     * Verifica si debe mostrarse el modal
     */
    check: function() {
      const result = this._evaluateConfig();

      if (result.shouldShow) {
        if (!this.isModalVisible) {
          this.isModalVisible = true;
          this.wasJoinedInProgress = result.joinedInProgress;

          this.currentData = {
            type: result.type,
            remainingSeconds: result.remainingSeconds,
            joinedInProgress: result.joinedInProgress
          };
          this.onShowModal(this.currentData);
        }
      } else {
        if (this.isModalVisible) {
          this.isModalVisible = false;
          const hideData = {
            joinedInProgress: this.wasJoinedInProgress
          };

          this.currentData = null;
          this.wasJoinedInProgress = false;
          this.onHideModal(hideData);
        }

        // Si todos los eventos están caducados, detener el interval
        if (result.allExpired) {
          console.log('[AlertScheduleManager] Todos los eventos han caducado. Deteniendo verificación.');
          this.stop();
        }
      }
    },

    /**
     * Evalúa la configuración y determina si mostrar alerta
     * @returns {Object} { shouldShow, type, remainingSeconds, allExpired }
     */
    _evaluateConfig: function() {
      // Configuración no disponible
      if (!this.config) {
        return { shouldShow: false, type: null, remainingSeconds: 0, allExpired: true };
      }

      // Llave deshabilitada
      if (this.config.enable !== true) {
        return { shouldShow: false, type: null, remainingSeconds: 0, allExpired: true };
      }

      const schedule = this.config.schedule;

      // Alerta permanente (sin schedule)
      if (!schedule || !Array.isArray(schedule) || schedule.length === 0) {
        return {
          shouldShow: true,
          type: 'permanent_no_schedule',
          remainingSeconds: -1,
          allExpired: false
        };
      }

      return this._evaluateSchedule(schedule);
    },

    /**
     * Evalúa el array de schedule
     * @param {Array} schedule
     * @returns {Object}
     */
    _evaluateSchedule: function(schedule) {
      const now = Date.now();
      let allExpired = true;

      for (let i = 0; i < schedule.length; i++) {
        const event = schedule[i];
        const result = this._evaluateEvent(event, now);

        // Si hay un evento activo, mostrarlo
        if (result.shouldShow) {
          result.allExpired = false;
          return result;
        }

        // Si el evento aún no ha expirado (es futuro o indefinido activo)
        if (!result.isExpired) {
          allExpired = false;
        }
      }

      // Ningún evento activo
      return {
        shouldShow: false,
        type: null,
        remainingSeconds: 0,
        allExpired: allExpired
      };
    },

    /**
     * Evalúa un evento individual del schedule
     * @param {Object} event - { start, visible_seconds }
     * @param {number} now - Timestamp actual
     * @returns {Object}
     */
    _evaluateEvent: function(event, now) {
      // Fecha mal formada - fallback a alerta permanente
      if (!event || typeof event.start === 'undefined') {
        return {
          shouldShow: true,
          type: 'invalid_format_fallback',
          remainingSeconds: -1,
          isExpired: false
        };
      }

      const startTimestamp = this._parseDate(event.start);

      // Fecha no parseable - fallback a alerta permanente
      if (startTimestamp === null) {
        return {
          shouldShow: true,
          type: 'invalid_format_fallback',
          remainingSeconds: -1,
          isExpired: false
        };
      }

      const visibleSeconds = this._parseVisibleSeconds(event.visible_seconds);

      // Alerta indefinida (sin visible_seconds)
      if (visibleSeconds === 0) {
        if (now >= startTimestamp) {
          return {
            shouldShow: true,
            type: 'indefinite',
            remainingSeconds: -1,
            isExpired: false
          };
        }
        // Evento futuro
        return { shouldShow: false, type: null, remainingSeconds: 0, isExpired: false };
      }

      // Alerta con duración
      const endTimestamp = startTimestamp + (visibleSeconds * 1000);

      // Evento activo
      if (now >= startTimestamp && now < endTimestamp) {
        const remainingMs = endTimestamp - now;
        const remainingSeconds = Math.ceil(remainingMs / 1000);
        const joinedInProgress = this.startedAt > startTimestamp;

        return {
          shouldShow: true,
          type: 'timed',
          remainingSeconds: remainingSeconds,
          isExpired: false,
          joinedInProgress: joinedInProgress
        };
      }

      // Evento futuro
      if (now < startTimestamp) {
        return { shouldShow: false, type: null, remainingSeconds: 0, isExpired: false };
      }

      // Evento vencido
      return { shouldShow: false, type: null, remainingSeconds: 0, isExpired: true };
    },

    /**
     * Parsea una fecha UTC
     * @param {string} dateString
     * @returns {number|null}
     */
    _parseDate: function(dateString) {
      if (!dateString || typeof dateString !== 'string') {
        return null;
      }

      try {
        const timestamp = new Date(dateString).getTime();

        if (isNaN(timestamp)) {
          return null;
        }

        return timestamp;
      } catch (e) {
        return null;
      }
    },

    /**
     * Parsea visible_seconds
     * @param {*} value
     * @returns {number}
     */
    _parseVisibleSeconds: function(value) {
      if (value === null || value === undefined) {
        return 0;
      }

      const parsed = parseInt(value, 10);

      if (isNaN(parsed) || parsed < 0) {
        return 0;
      }

      return parsed;
    },

    /**
     * ¿El modal está visible?
     * @returns {boolean}
     */
    isVisible: function() {
      return this.isModalVisible;
    },

    /**
     * ¿El cron está corriendo?
     * @returns {boolean}
     */
    isRunning: function() {
      return this.intervalId !== null;
    },

    /**
     * Obtiene el estado actual
     * @returns {Object}
     */
    getStatus: function() {
      return {
        isRunning: this.intervalId !== null,
        isModalVisible: this.isModalVisible,
        currentData: this.currentData,
        config: this.config
      };
    }
  };

  return {
    getInstance: function() {
      if (!instance) {
        instance = new AlertScheduleManagerClass();
      }
      return instance;
    }
  };

})();



// WEBPACK FOOTER //
// ./src/components/Modal/functions/AAFModalDeviceChangeKaon/AlertScheduleManager.js